// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import type { CryptoEngine } from "./crypto";
import { KEY_PREFIX, userKeyPrefix } from "./keyring";
import { openPriv, sealPriv } from "./keystore";

// Opt-in encrypt-keys-at-rest (Phase 6, XSS hardening). When enabled, the device
// keyring is NOT stored as plaintext `zalmoxis.key.*` slots; instead a single
// `zalmoxis.keylock.<userId>` blob holds the whole keyring AEAD-encrypted under a passphrase
// (reusing the key-backup machinery). The passphrase is never stored — it's
// prompted once per session to unlock, defeating passive/snapshot key theft. It
// can't stop an ACTIVE XSS (the unlocked key is in worker memory while running).
// Per-user (the lock key + the slot scans are scoped by userId), so one account on a shared
// device is never prompted for another account's keystore.

const LOCK_PREFIX = "zalmoxis.keylock.";
const lockKey = (userId: string): string => LOCK_PREFIX + userId;

export interface KeyRecord {
  deviceId: string; // the `zalmoxis.key.<deviceId>` slot suffix (deviceId is itself `<userId>/<rand>`)
  priv: string;
  pub?: string;
  fingerprint?: string;
}

// After unlocking (or (re)locking), the decrypted records are held in memory for
// the session so features that need the private keys — e.g. key backup — can reach
// them WITHOUT re-prompting and WITHOUT writing plaintext back to localStorage.
let unlockedRecords: KeyRecord[] | null = null;

// unlockedKeyringPrivs returns the armored private keys unlocked this session
// (empty until the keyring has been unlocked). Use this for backup when isLocked().
export function unlockedKeyringPrivs(): string[] {
  return (unlockedRecords ?? []).map((r) => r.priv).filter(Boolean);
}

// isLocked reports whether THIS USER's keyring is stored encrypted at rest.
export function isLocked(userId: string): boolean {
  return localStorage.getItem(lockKey(userId)) !== null;
}

// readPlaintextSlots returns this user's device-key records, unsealing each slot's at-rest seal
// (legacy plaintext passes through). Exported so the bio-lock tier (biolock.ts) reuses the exact
// same slot collection as the passphrase keylock.
export async function readPlaintextSlots(userId: string): Promise<KeyRecord[]> {
  const out: KeyRecord[] = [];
  const prefix = userKeyPrefix(userId);
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (!k?.startsWith(prefix)) continue;
    try {
      const v = JSON.parse(localStorage.getItem(k) || "{}");
      if (v.priv) out.push({ deviceId: k.slice(KEY_PREFIX.length), priv: await openPriv(v.priv), pub: v.pub, fingerprint: v.fingerprint });
    } catch {
      /* skip malformed slot */
    }
  }
  return out;
}

// removePlaintextSlots deletes every one of this user's device-key slots (after the records have
// been moved into a lock blob). Exported for the bio-lock tier.
export function removePlaintextSlots(userId: string): void {
  const prefix = userKeyPrefix(userId);
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k?.startsWith(prefix)) keys.push(k);
  }
  for (const k of keys) localStorage.removeItem(k);
}

// writePlaintextSlot stores a record's key SEALED at rest (the slot key is already namespaced by
// user, since r.deviceId is `<userId>/<rand>`). "Plaintext" here is relative to the keylock blob —
// disabling the lock returns keys to the (sealed) slot store, not literal cleartext.
export async function writePlaintextSlot(r: KeyRecord): Promise<void> {
  localStorage.setItem(`${KEY_PREFIX}${r.deviceId}`, JSON.stringify({ priv: await sealPriv(r.priv), pub: r.pub, fingerprint: r.fingerprint }));
}

// lockKeyring encrypts every slot key of the user into the passphrase keylock and removes the
// slots. Returns how many keys were locked.
export async function lockKeyring(engine: CryptoEngine, passphrase: string, userId: string): Promise<number> {
  const records = await readPlaintextSlots(userId);
  await relock(engine, passphrase, records, userId);
  removePlaintextSlots(userId);
  return records.length;
}

// unlockKeyring decrypts the user's keylock to its key records (throws on wrong passphrase).
export async function unlockKeyring(engine: CryptoEngine, passphrase: string, userId: string): Promise<KeyRecord[]> {
  const blob = localStorage.getItem(lockKey(userId));
  if (!blob) return [];
  const { keyring } = await engine.restoreKeyring(passphrase, blob);
  const records = JSON.parse(keyring) as KeyRecord[];
  unlockedRecords = records; // keep in memory so backup can reach them while locked
  return records;
}

// relock re-encrypts an updated record set under the passphrase (e.g. after adding
// a freshly-generated device key while locked).
export async function relock(engine: CryptoEngine, passphrase: string, records: KeyRecord[], userId: string): Promise<void> {
  const { blob } = await engine.backupKeyring(passphrase, JSON.stringify(records));
  localStorage.setItem(lockKey(userId), blob);
  unlockedRecords = records; // keep the in-memory view current (e.g. after enabling the lock)
}

// disableLock decrypts the user's keyring, rewrites the plaintext slots, and clears the
// lock (throws on wrong passphrase).
export async function disableLock(engine: CryptoEngine, passphrase: string, userId: string): Promise<void> {
  const records = await unlockKeyring(engine, passphrase, userId);
  for (const r of records) await writePlaintextSlot(r);
  localStorage.removeItem(lockKey(userId));
  unlockedRecords = null; // keys are plaintext again; collectLocalKeyring reads them
}
